import { parseIdentityClaims } from "@istari-frontend/auth";

/**
 * The signed-in caller's keys into the registry's user records. `sub` cannot serve: once identity-service
 * issues its own principal id as `sub`, it no longer equals the registry's provider user id.
 */
export type CurrentUserIdentity = {
  /** identity-service's stable principal id; absent on Zitadel-direct tokens. */
  principalId?: string;
  /** The identity provider's user id: the `upstream_user_id` claim, else `sub` on tokens without it. */
  upstreamUserId?: string;
};

type RegistryUserKeys = { principalId?: string | null; providerUserId: string };

export function currentUserIdentityFromProfile(
  profile: (Record<string, unknown> & { sub?: string }) | undefined,
): CurrentUserIdentity {
  const claims = parseIdentityClaims(profile);
  return {
    principalId: claims.principalId,
    upstreamUserId: claims.upstreamUserId ?? (profile?.sub || undefined),
  };
}

export function isCurrentRegistryUser(user: RegistryUserKeys, identity: CurrentUserIdentity): boolean {
  if (identity.principalId && user.principalId === identity.principalId) return true;
  return !!identity.upstreamUserId && user.providerUserId === identity.upstreamUserId;
}

// Principal first: a row the registry has already dual-keyed wins over a stale provider id match.
export function findCurrentRegistryUser<T extends RegistryUserKeys>(
  users: readonly T[] | undefined,
  identity: CurrentUserIdentity,
): T | undefined {
  if (!users) return undefined;
  const byPrincipal = identity.principalId ? users.find((u) => u.principalId === identity.principalId) : undefined;
  return byPrincipal ?? users.find((u) => !!identity.upstreamUserId && u.providerUserId === identity.upstreamUserId);
}
